import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { filterActiveMasters } from "../utils/masterFilters";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const WagesRegisterLegalDeptWise = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // States
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [deptList, setDeptList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("");

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial masters
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
    Fn_FillListData(dispatch, setDeptList, "gridData", `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Id/0`);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear, selectedDept);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear, selectedDept]);

  const loadReportData = async (firmId, monthName, year, departmentId) => {
    setIsLoading(true);
    try {
      if (monthName && year) {
        const monthNum = MONTH_NAMES.indexOf(monthName) + 1;
        const monthStr = String(monthNum);

        const fd = new FormData();
        fd.append("F_FirmMaster", firmId);
        fd.append("PayrollMonth", monthStr);
        fd.append("PayrollYear", year);
        fd.append("F_DepartmentMaster", departmentId || 0);

        // Fetching Wages Register Legal data
        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetWagesRegisterLegal_DepartmentWise/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );

        let parsedData = null;
        if (records && records.length > 0) {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey !== undefined && firstKey !== null) {
            const jsonString = records.map(row => row[firstKey] || "").join("");
            if (jsonString) {
              const rawObj = JSON.parse(jsonString);
              parsedData = rawObj.data || rawObj;
            }
          }
        }
        setReportData(parsedData);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getFirmEsiNo = () => {
    if (reportData?.FirmDetails?.ESINo) return reportData.FirmDetails.ESINo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.ESINo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  // Grouping and mapping logic
  const getGroupedDepartments = () => {
    if (!reportData || !reportData.Departments) return [];

    return reportData.Departments.map(dept => {
      const deptName = dept.DepartmentName?.toUpperCase() || "UNASSIGNED STAFF";
      const records = dept.Records || dept.Employees || [];
      const query = searchTerm.toLowerCase();

      const mappedRecords = [];
      let totalWD = 0, totalPaid = 0, totalGrossRate = 0, totalGrossEarned = 0, totalPF = 0, totalESI = 0, totalNet = 0;

      records.forEach(rec => {
        // Search Filter
        const matchSearch = (rec.EmployeeName || rec.Name)?.toLowerCase().includes(query) || 
                            (rec.FatherName)?.toLowerCase().includes(query) || 
                            (rec.SerialNo || rec.AcNo || String(rec.Sno))?.toLowerCase().includes(query);
        if (!matchSearch) return;

        const row = {
          sno: rec.Sno || 0,
          acNo: rec.SerialNo || rec.AcNo || "",
          name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
          fatherName: (rec.FatherName || "").toUpperCase(),
          gender: rec.Sex || rec.Gender || "M",
          designation: rec.Designation || "STAFF",
          pfNo: rec.PFNo || "N/A",
          uan: rec.UAN || "N/A",
          esiNo: rec.ESINo || "N/A",
          cardNo: rec.EmployeeCode || rec.CardNo || "N/A",
          // Attendance
          wd: parseFloat(rec.WorkedDay || rec.WD || 0),
          lw: parseFloat(rec.Absent || rec.LW || 0),
          fh: parseFloat(rec.FestiveHoliday || rec.FH || 0),
          wh: parseFloat(rec.WeeklyHoliday || rec.WH || 0),
          cl: parseFloat(rec.CL || 0),
          ml: parseFloat(rec.ML || 0),
          paid: parseFloat(rec.PaidDays || rec.PaidDay || 0),
          ab: parseFloat(rec.AbsentDays || rec.AB || 0),
          // Rates
          basicRate: parseFloat(rec.BasicRate || rec.BasicSalary || 0),
          daRate: parseFloat(rec.DARate || 0),
          hraRate: parseFloat(rec.HRARate || rec.HRA || 0),
          conveyanceRate: parseFloat(rec.ConveyanceRate || rec.Conveyance || 0),
          // Earned Wages
          earnedBasic: parseFloat(rec.EarnedBasic || rec.Wages || 0),
          earnedDA: parseFloat(rec.EarnedDA || 0),
          earnedHRA: parseFloat(rec.EarnedHRA || rec.CalculatedAllowance1 || 0),
          earnedConveyance: parseFloat(rec.EarnedConveyance || rec.CalculatedAllowance2 || 0),
          // Other Earnings
          othAll4: parseFloat(rec.OthAll4 || rec.CalculatedAllowance4 || 0),
          otHours: parseFloat(rec.OTHours || 0),
          otAmount: parseFloat(rec.OTAmount || rec.OverTime || 0),
          arrear: parseFloat(rec.Arrear || 0),
          bonus: parseFloat(rec.Bonus || rec.CalculatedAllowance3 || 0),
          grossWages: parseFloat(rec.GrossWages || rec.GrossAmount || 0),
          // Deductions
          loanAdv: parseFloat(rec.LoanAdv || 0),
          tds: parseFloat(rec.TDS || 0),
          recovery: parseFloat(rec.Recovery || 0),
          pf: Math.round(parseFloat(rec.PF || rec.PFDeduction || 0)),
          pension: Math.round(parseFloat(rec.Pension || 0)),
          esi: parseFloat(rec.ESI || rec.ESIDeduction || 0),
          totalDed: parseFloat(rec.TotalDed || rec.TotalDeduction || 0),
          netPayable: parseFloat(rec.NetPayable || rec.NetPayment || 0)
        };

        mappedRecords.push(row);
        totalWD += row.wd;
        totalPaid += row.paid;
        totalGrossRate += (row.basicRate + row.daRate + row.hraRate + row.conveyanceRate);
        totalGrossEarned += row.grossWages;
        totalPF += row.pf;
        totalESI += row.esi;
        totalNet += row.netPayable;
      });

      return {
        name: deptName,
        records: mappedRecords,
        totals: {
          wd: totalWD,
          paid: totalPaid,
          grossRate: totalGrossRate,
          grossEarned: totalGrossEarned,
          pf: totalPF,
          esi: totalESI,
          net: totalNet
        }
      };
    }).filter(d => d.records.length > 0);
  };

  const departments = getGroupedDepartments();

  // Excel Export matching legal WagesRegister format
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Wages Sheet");

      worksheet.addRow([sessionFirm?.Name?.toUpperCase() || ""]);
      worksheet.addRow(["SALARY/WAGES SHEET (Applicable to all Labour Act)"]);
      worksheet.addRow([`PF CODE: ${getFirmPfNo()}`, `ESI CODE: ${getFirmEsiNo()}`, "", "", "", "", "", `Month: ${selectedMonth}, ${selectedYear}`]);
      worksheet.addRow([]);

      const headerRow1 = worksheet.addRow(["Sn", "EMPLOYEE NAME", "WD", "PAID", "RATE / WAGES", "Oth.All4", "Arrear Allowance", "Loan/Adv", "ESI", "NET PAYABLE", "SIGNATURE"]);
      const headerRow2 = worksheet.addRow(["", "FATHER'S NAME", "LW", "AB", "", "CRL Days", "Adv. Bonus", "TDS", "TOTAL DED.", "", ""]);
      const headerRow3 = worksheet.addRow(["", "DESIGNATION", "FH", "CL", "ML", "D.A.", "OT Hours", "GROSS WAGES", "Recovery", "PF", ""]);
      const headerRow4 = worksheet.addRow(["", "PF No / UAN", "WH", "", "", "HRA", "OT Amount", "", "", "Pen", ""]);
      const headerRow5 = worksheet.addRow(["", "ESINo / Emp Code", "", "", "", "CON", "Arrear", "", "", "", ""]);

      [headerRow1, headerRow2, headerRow3, headerRow4, headerRow5].forEach(row => {
        row.font = { bold: true, size: 9, color: { argb: "FFFFFFFF" } };
        row.eachCell(cell => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2C3E50" } };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });
      });

      departments.forEach(dept => {
        worksheet.addRow([`DEPARTMENT: ${dept.name}`]).font = { bold: true };
        dept.records.forEach(rec => {
          const r1 = worksheet.addRow([rec.sno, rec.name, rec.wd, rec.paid, rec.basicRate, rec.othAll4, rec.arrear, rec.loanAdv, rec.esi, rec.netPayable, ""]);
          const r2 = worksheet.addRow(["", rec.fatherName, rec.lw, rec.ab, rec.earnedBasic, rec.cl, rec.bonus, rec.tds, rec.totalDed, "", ""]);
          const r3 = worksheet.addRow(["", rec.designation, rec.fh, rec.cl, rec.ml, rec.daRate, rec.otHours, rec.grossWages, rec.recovery, rec.pf, ""]);
          const r4 = worksheet.addRow(["", `PF:${rec.pfNo}/UAN:${rec.uan}`, rec.wh, "", "", rec.hraRate, rec.otAmount, "", "", rec.pension, ""]);
          const r5 = worksheet.addRow(["", `ESI:${rec.esiNo}/EmpCode:${rec.cardNo}`, "", "", "", rec.conveyanceRate, rec.arrear, "", "", "", ""]);

          [r1, r2, r3, r4, r5].forEach(row => {
            row.eachCell(cell => {
              cell.alignment = { vertical: "middle", horizontal: "center" };
            });
          });
        });
        
        // Add dept summary
        worksheet.addRow([`Total Employees: ${dept.records.length}`, "", `Total WD: ${dept.totals.wd}`, `Total Paid: ${dept.totals.paid}`, `Total Gross Earned: ${dept.totals.grossEarned}`, "", "", "", `Total ESI: ${dept.totals.esi}`, `Net Payable: ${dept.totals.net}`]).font = { bold: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Wages_Register_Legal_${selectedMonth}_${selectedYear}.xlsx`;
      link.click();
    } catch (e) {
      console.error(e);
      alert("Excel export failed.");
    }
  };

  // PDF Export
  const exportToPDF = () => {
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.setFontSize(11);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 148, 12, { align: "center" });
    pdf.text(`SALARY/WAGES SHEET (Applicable to all Labour Act) - ${selectedMonth?.toUpperCase()} ${selectedYear}`, 148, 17, { align: "center" });

    pdf.setFontSize(8);
    pdf.text(`PF CODE: ${getFirmPfNo()}`, 14, 23);
    pdf.text(`ESI CODE: ${getFirmEsiNo()}`, 220, 23);

    const body = [];
    departments.forEach(dept => {
      body.push([
        { content: `DEPARTMENT: ${dept.name}`, colSpan: 11, styles: { fontStyle: "bold", fillColor: [240, 240, 240] } }
      ]);

      dept.records.forEach((rec) => {
        body.push([
          rec.sno,
          `${rec.name}\n${rec.fatherName}\n${rec.designation}\nPF:${rec.pfNo}/UAN:${rec.uan}\nESI:${rec.esiNo}/EmpCode:${rec.cardNo}`,
          `WD:${rec.wd}\nLW:${rec.lw}\nFH:${rec.fh}\nWH:${rec.wh}`,
          `PAID:${rec.paid}\nAB:${rec.ab}\nCL:${rec.cl}\nML:${rec.ml}`,
          `RATE / WAGES\nBasic: ${rec.basicRate}/${rec.earnedBasic}\nDA: ${rec.daRate}/${rec.earnedDA}\nHRA: ${rec.hraRate}/${rec.earnedHRA}\nCON: ${rec.conveyanceRate}/${rec.earnedConveyance}`,
          `Oth.All4: ${rec.othAll4}\nOT Hours: ${rec.otHours}\nOT Amt: ${rec.otAmount}\nArrear: ${rec.arrear}`,
          `Arrear: ${rec.arrear}\nBonus: ${rec.bonus}\nGross Wages: ${rec.grossWages}`,
          `Loan/Adv: ${rec.loanAdv}\nTDS: ${rec.tds}\nRecovery: ${rec.recovery}`,
          `PF: ${rec.pf}\nPension: ${rec.pension}`,
          `ESI: ${rec.esi}\nTotal Ded: ${rec.totalDed}`,
          rec.netPayable.toFixed(2)
        ]);
      });

      // Total Row
      body.push([
        { content: `TOTAL ${dept.name}`, colSpan: 2, styles: { fontStyle: "bold", halign: "right" } },
        dept.totals.wd.toFixed(1),
        dept.totals.paid.toFixed(1),
        "", "",
        dept.totals.grossEarned.toFixed(2),
        "",
        dept.totals.pf.toString(),
        dept.totals.esi.toFixed(2),
        dept.totals.net.toFixed(2)
      ]);
    });

    autoTable(pdf, {
      head: [[
        "Sn", "Employee Details", "Att.", "Paid", "Rates vs Earned", "Oth/OT", "Gross Wages", "Advances", "PF/Pen", "ESI/Total Ded", "Net Payable"
      ]],
      body,
      startY: 27,
      styles: { fontSize: 6.5, cellPadding: 1, lineColor: [0,0,0], lineWidth: 0.05 },
      headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] }
    });

    pdf.save(`Wages_Register_Legal_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-wages-legal-container, #print-wages-legal-container * {
        visibility: visible !important;
      }
      #print-wages-legal-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .wages-print-table {
        width: 100%;
        border-collapse: collapse;
      }
      .wages-print-table th, .wages-print-table td {
        border: 1px solid #000;
        padding: 4px;
        font-size: 8px;
        text-align: center;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="Wages Register Legal" motherMenu="Reports" pageContent="Legal Salary Sheet" />
      </div>

      {/* Filters */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Department */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Department</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
              >
                <option value="">All Departments</option>
                {filterActiveMasters(deptList, selectedDept).map((d) => (
                  <option key={d.Id} value={d.Id}>{d.Name}</option>
                ))}
              </select>
            </Col>

            {/* Month */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={2} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Name, UAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* Web View */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px", overflow: "hidden" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16">
                    Wages Register Legal Sheet
                  </h5>
                  <small className="text-muted">
                    PF Code: <strong>{getFirmPfNo()}</strong> &nbsp;|&nbsp; ESI Code: <strong>{getFirmEsiNo()}</strong>
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="danger" size="sm" onClick={exportToPDF}>PDF</Button>
                  <Button variant="success" size="sm" onClick={exportToExcel}>Excel</Button>
                  <Button variant="primary" size="sm" onClick={() => window.print()}>Print</Button>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading Wages Register Legal...</div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover bordered className="mb-0 text-center text-nowrap" style={{ fontSize: "11px" }}>
                      <thead className="bg-light">
                        <tr>
                          <th>Sn</th>
                          <th>Employee details</th>
                          <th>Att. (WD/LW/FH/WH)</th>
                          <th>Paid (CL/ML)</th>
                          <th>Rate vs Earned</th>
                          <th>Oth/OT</th>
                          <th>Gross Wages</th>
                          <th>Advances (TDS/Rec)</th>
                          <th>PF / Pension</th>
                          <th>ESI / Total Ded</th>
                          <th>Net Payable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departments.length === 0 ? (
                          <tr>
                            <td colSpan={11} className="py-5 text-muted">No data found.</td>
                          </tr>
                        ) : (
                          departments.map(dept => (
                            <Fragment key={dept.name}>
                              <tr className="bg-light-subtle">
                                <td colSpan={11} className="text-start fw-bold text-primary py-2 px-3">
                                  DEPARTMENT: {dept.name}
                                </td>
                              </tr>
                              {dept.records.map((row) => (
                                <tr key={row.acNo}>
                                  <td>{row.sno}</td>
                                  <td className="text-start">
                                    <div className="fw-bold">{row.name}</div>
                                    <div className="text-muted">{row.fatherName}</div>
                                    <div className="text-muted font-monospace">{row.designation} | UAN: {row.uan}</div>
                                  </td>
                                  <td>
                                    WD: {row.wd} | LW: {row.lw} <br /> FH: {row.fh} | WH: {row.wh}
                                  </td>
                                  <td>
                                    Paid: {row.paid} | AB: {row.ab} <br /> CL: {row.cl} | ML: {row.ml}
                                  </td>
                                  <td>
                                    Basic: {row.basicRate} / {row.earnedBasic} <br />
                                    HRA: {row.hraRate} / {row.earnedHRA}
                                  </td>
                                  <td>
                                    Oth.All4: {row.othAll4} <br />
                                    OT Amt: {row.otAmount} (Hrs: {row.otHours})
                                  </td>
                                  <td className="fw-bold">₹{row.grossWages}</td>
                                  <td>
                                    TDS: {row.tds} <br /> Recovery: {row.recovery}
                                  </td>
                                  <td>
                                    PF: {row.pf} <br /> Pension: {row.pension}
                                  </td>
                                  <td>
                                    ESI: {row.esi} <br /> Total Ded: {row.totalDed}
                                  </td>
                                  <td className="fw-bold text-success fs-13">₹{row.netPayable}</td>
                                </tr>
                              ))}
                            </Fragment>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Printable Layout */}
          <div id="print-wages-legal-container" className="d-none">
            <h5 style={{ textAlign: "center", marginBottom: "2px" }}>{sessionFirm?.Name?.toUpperCase()}</h5>
            <h6 style={{ textAlign: "center", marginTop: "2px" }}>SALARY/WAGES SHEET (Applicable to all Labour Act)</h6>
            <div style={{ display: "flex", justifyContent: "space-between", margin: "10px 0", fontSize: "10px" }}>
              <div>PF CODE: {getFirmPfNo()}</div>
              <div>ESI CODE: {getFirmEsiNo()}</div>
              <div>Month: {selectedMonth?.toUpperCase()} {selectedYear}</div>
            </div>

            <table className="wages-print-table">
              <thead>
                <tr>
                  <th>Sn</th>
                  <th>Employee details</th>
                  <th>Attendance</th>
                  <th>Paid / Leaves</th>
                  <th>Rates vs Earned</th>
                  <th>Oth/OT</th>
                  <th>Gross Wages</th>
                  <th>Advances</th>
                  <th>PF/Pen</th>
                  <th>ESI/Total Ded</th>
                  <th>Net Payable</th>
                </tr>
              </thead>
              <tbody>
                {departments.map(dept => (
                  <Fragment key={dept.name}>
                    <tr style={{ background: "#eee", fontWeight: "bold" }}>
                      <td colSpan={11} style={{ textAlign: "left" }}>DEPARTMENT: {dept.name}</td>
                    </tr>
                    {dept.records.map(row => (
                      <tr key={row.acNo}>
                        <td>{row.sno}</td>
                        <td style={{ textAlign: "left" }}>
                          <strong>{row.name}</strong> / {row.fatherName}<br />
                          {row.designation} | UAN: {row.uan}
                        </td>
                        <td>WD:{row.wd} | LW:{row.lw}<br />FH:{row.fh} | WH:{row.wh}</td>
                        <td>Paid:{row.paid} | AB:{row.ab}<br />CL:{row.cl} | ML:{row.ml}</td>
                        <td>Basic:{row.basicRate}/{row.earnedBasic}<br />HRA:{row.hraRate}/{row.earnedHRA}</td>
                        <td>Oth:{row.othAll4} | OT:{row.otAmount}</td>
                        <td><strong>₹{row.grossWages}</strong></td>
                        <td>TDS:{row.tds} | Rec:{row.recovery}</td>
                        <td>PF:{row.pf} | Pen:{row.pension}</td>
                        <td>ESI:{row.esi} | Ded:{row.totalDed}</td>
                        <td><strong>₹{row.netPayable}</strong></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default WagesRegisterLegalDeptWise;
